import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { showToastNotification } from "@/utility/helper";
import { ThrowError } from "@/api/callApi";
import { RoutesLink } from "@/utility/RouteLinks";
import { errorTypes } from "@/utility/validation";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import Card from "@/components/common/Card";
import FormField from "@/components/common/FormField";
import { Button } from "@/components/ui/button";
import { CompanyDocumentServices } from "@/services/CompanyDocument/CompanyDocumentServices";
import type { ICompanyDocumentPayload } from "@/interface/companyDocument.interface";
import FullPageLoader from "@/components/common/FullPageLoader";

const CreateCompanyDocument: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isFatchLoading, setIsFatchLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ICompanyDocumentPayload>({
    defaultValues: {
      type: "POLICY",
      title: "",
      documentKey: "",
      description: "",
      content: "<p>Enter document content here...</p>",
      version: 1.0,
      effectiveDate: new Date().toISOString().split("T")[0],
      isActive: true,
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (!isEditMode || !id) return;

    const fetchData = async () => {
      setIsFatchLoading(true);
      try {
        const response =
          await CompanyDocumentServices.getCompanyDocumentById(id);
        if (response.status && response.data) {
          const data = response.data;
          setValue("type", data.type);
          setValue("title", data.title);
          setValue("documentKey", data.documentKey);
          setValue("description", data.description || "");
          setValue("content", data.content);
          setValue("version", Number(data.version));
          setValue("effectiveDate", data.effectiveDate);
          setValue("isActive", data.isActive);
        } else {
          showToastNotification("danger", "Failed to load document details");
          navigate(-1);
        }
      } catch (error) {
        ThrowError(error);
        navigate(-1);
      } finally {
        setIsFatchLoading(false);
      }
    };
    fetchData();
  }, [id, isEditMode, setValue, navigate]);

  const onSubmit = async (data: ICompanyDocumentPayload) => {
    setIsLoading(true);
    try {
      let response;
      if (isEditMode && id) {
        response = await CompanyDocumentServices.updateCompanyDocument(
          data,
          id,
        );
      } else {
        response = await CompanyDocumentServices.createCompanyDocument(data);
      }
      if (response.status) {
        showToastNotification(
          "success",
          response.message ||
          (isEditMode
            ? "Document updated successfully"
            : "Document created successfully"),
        );
        navigate(RoutesLink.companyDocument);
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isFatchLoading) {
    return (
      <Layout>
        {/* <div className="flex items-center justify-center min-h-[400px]"> */}
        <FullPageLoader />
        {/* <Spinner className="h-8 w-8 text-primary animate-spin" /> */}
        {/* </div> */}
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <MainHeading
          title={
            isEditMode ? "Edit Company Document" : "Create Company Document"
          }
          isBackButtonVisible
          onClick={() => navigate(-1)}
        />
        <Card className="overflow-visible">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                {...register("type", { ...errorTypes.required("Type") })}
                type="select"
                label="Type"
                value={String(watch("type") || "")}
                options={[
                  { value: "POLICY", label: "Policy" },
                  { value: "TERMS_CONDITION", label: "Terms & Condition" },
                  { value: "LETTER", label: "Letter" },
                ]}
                error={errors.type?.message as string}
                required
              />

              <FormField
                {...register("title", { ...errorTypes.required("Title"), ...errorTypes?.name })}
                type="text"
                label="Title"
                minLength={4}
                maxLength={50}
                placeholder="Enter document title"
                error={errors.title?.message as string}
                required
              />

              <FormField
                {...register("documentKey", {
                  ...errorTypes.required("Document Key"),
                  ...errorTypes?.name
                })}
                type="text"
                label="Document Key"
                minLength={4}
                maxLength={20}
                placeholder="e.g., leave-policy"
                error={errors.documentKey?.message as string}
                required
              />

              <FormField
                {...register("description", {
                  ...errorTypes.required("Description"),
                  ...errorTypes.description,
                })}
                type="textarea"
                label="Description"
                rows={3}
                minLength={10}
                maxLength={500}
                placeholder="Brief description"
                error={errors.description?.message as string}
                required
              />

              <FormField
                {...register("version", {
                  valueAsNumber: true,
                  ...errorTypes.required("Version"),
                })}
                type="number"
                label="Version"
                placeholder="1.0"
                step="0.1"
                min="1.0"
                error={errors.version?.message as string}
                required
              />

              <FormField
                {...register("effectiveDate", {
                  ...errorTypes.required("Effective Date"),
                })}
                type="date"
                label="Effective Date"
                error={errors.effectiveDate?.message as string}
                required
              />

              <div className="col-span-full">
                <FormField
                  {...register("isActive")}
                  type="checkbox"
                  label="Active"
                  error={errors.isActive?.message as string}
                  required
                />
              </div>

              <div className="col-span-full">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="content"
                  control={control}
                  rules={{ ...errorTypes.required("Content") }}
                  render={({ field }) => (
                    <CKEditor
                      editor={ClassicEditor as any}
                      data={field.value}
                      onChange={(_, editor) => {
                        const data = editor.getData();
                        field.onChange(data);
                      }}
                      config={{
                        placeholder: "Write document content here...",
                        toolbar: [
                          "heading",
                          "|",
                          "bold",
                          "italic",
                          "underline",
                          "strikethrough",
                          "|",
                          "bulletedList",
                          "numberedList",
                          "|",
                          "link",
                          "blockQuote",
                          "|",
                          "undo",
                          "redo",
                        ],
                      }}
                    />
                  )}
                />
                {errors.content && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.content.message as string}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading
                  ? isEditMode
                    ? "Updating..."
                    : "Creating..."
                  : isEditMode
                    ? "Update Document"
                    : "Create Document"}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Layout>
  );
};

export default CreateCompanyDocument;
